'use client';

import { Buildings } from 'phosphor-icons';
import { useLayoutEffect, useRef, useState } from 'react';
import { interpolateStringWithData } from 'utils';
import { c } from '../../../utils/tailwind-merge';
import { Avatar } from '../../atoms/Avatar/Avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../atoms/Tooltip/TooltipRadix';
import type {
  AccountListTabsProps,
  AccountListTabsValue,
} from './AccountListTabs.types';

type TruncatedTextWithTooltipProps = {
  text: string;
  isSelected: boolean;
};

const TruncatedTextWithTooltip = ({
  text,
  isSelected,
}: TruncatedTextWithTooltipProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [text]);

  const reference = (
    <span
      className={c(
        'block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-left text-xs font-normal',
        isSelected ? 'text-grey-900' : 'text-grey-600'
      )}
      ref={ref}
    >
      {text}
    </span>
  );

  if (!isTruncated) {
    return reference;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{reference}</TooltipTrigger>
      <TooltipContent
        arrowClassName="fill-grey-700"
        className="max-w-[250px] break-words rounded-sm bg-grey-700 px-4 py-3 text-xs text-white"
      >
        {text}
      </TooltipContent>
    </Tooltip>
  );
};

type AnchorTabProps = {
  href: string;
  ariaLabel: string;
  isSelected: boolean;
  label: string;
  subtitle: string;
  avatar: React.ReactNode;
  onClick: () => void;
};

const AnchorTab = ({
  href,
  ariaLabel,
  isSelected,
  label,
  subtitle,
  avatar,
  onClick,
}: AnchorTabProps) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onClick();
    const targetId = href.replace('#', '');
    document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <a
      href={href}
      aria-label={ariaLabel}
      aria-current={isSelected ? 'true' : undefined}
      onClick={handleClick}
      className={c(
        'relative flex cursor-pointer items-center gap-2 p-2',
        'transition-colors focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-blue focus-visible:ring-offset-2',
        'border-b-2',
        isSelected ? 'border-blue' : 'border-transparent'
      )}
    >
      <div className="flex min-w-0 flex-col items-start">
        <span
          className={c(
            'text-base font-normal',
            isSelected ? 'text-grey-900' : 'text-grey-700'
          )}
        >
          {label}
        </span>
        <div className="flex items-center gap-2">
          {avatar}
          <TruncatedTextWithTooltip text={subtitle} isSelected={isSelected} />
        </div>
      </div>
    </a>
  );
};

export const AccountListTabs = ({
  userName,
  userProfilePicture,
  publisherName,
  personalCount,
  organizationCount,
  value,
  personalSectionId,
  organizationSectionId,
  microcopy,
  onValueChange,
  className = '',
}: AccountListTabsProps) => {
  const isPersonalSelected = value === 'personal';
  const isOrganizationSelected = value === 'organization';

  const personalLabel = interpolateStringWithData(microcopy.personalLists, {
    count: String(personalCount),
  });

  const organizationLabel = interpolateStringWithData(
    microcopy.organizationLists,
    {
      count: String(organizationCount),
    }
  );

  const handlePersonalClick = () => {
    onValueChange('personal' as AccountListTabsValue);
  };

  const handleOrganizationClick = () => {
    onValueChange('organization' as AccountListTabsValue);
  };

  return (
    <nav aria-label={microcopy.ariaLabel} className={c('flex', className)}>
      <div
        className="flex gap-4"
        role="tablist"
        aria-label={microcopy.ariaLabel}
      >
        <AnchorTab
          href={`#${personalSectionId}`}
          ariaLabel={`${personalLabel}: ${userName}`}
          isSelected={isPersonalSelected}
          label={personalLabel}
          subtitle={userName}
          onClick={handlePersonalClick}
          avatar={
            <Avatar
              userName={userName}
              userProfilePicture={userProfilePicture}
              size={16}
              className={c(
                'avatar-icon font-medium',
                isPersonalSelected ? 'bg-blue' : 'bg-grey-100'
              )}
              textClassName={c(
                'text-[7px]',
                isPersonalSelected ? 'text-white' : 'text-grey-600'
              )}
            />
          }
        />
        <AnchorTab
          href={`#${organizationSectionId}`}
          ariaLabel={`${organizationLabel}: ${publisherName}`}
          isSelected={isOrganizationSelected}
          label={organizationLabel}
          subtitle={publisherName}
          onClick={handleOrganizationClick}
          avatar={
            <div
              className={c(
                'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full',
                isOrganizationSelected ? 'bg-blue' : 'bg-grey-100'
              )}
            >
              <Buildings
                size={8}
                weight="bold"
                aria-hidden="true"
                focusable="false"
                className={
                  isOrganizationSelected ? 'text-white' : 'text-grey-600'
                }
              />
            </div>
          }
        />
      </div>
    </nav>
  );
};

AccountListTabs.displayName = 'AccountListTabs';
